import {
  cn,
  Dialog as V2Dialog,
  DialogContent as V2DialogContent,
  DialogHeader as V2DialogHeader,
  DialogTitle as V2DialogTitle,
} from '@foresightdata/web-common-components';
import { Dialog, DialogContent, DialogTitle, IconButton, SxProps, Typography, useTheme } from '@mui/material';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { MaggieFeatureFlags } from '../../util/feature-flags';

const Title = styled('span')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export interface IFormDialogProps extends PropsWithChildren {
  lightDismiss?: boolean;
  disableRestoreFocus?: boolean;
  fullWidth?: DialogProps['fullWidth'];
  maxWidth?: DialogProps['maxWidth'];
  onClose?: () => void;
  open: boolean;
  showTitleBar?: boolean;
  /** MUI only; the web-common dialog takes no `sx`. */
  sx?: SxProps;
  title?: string;
  titleComponent?: ReactNode;
}

/**
 * The app's dialog. MUI's Dialog today; with the `useV2Components` flag on (FD-6109),
 * web-common's Dialog under the maggie-og theme, which draws MUI's paper, title bar and
 * `maxWidth` widths. Call sites are unchanged.
 */
export function BasicDialog(props: IFormDialogProps) {
  const {
    open,
    children,
    fullWidth,
    maxWidth = false,
    onClose,
    title,
    titleComponent,
    sx,
    disableRestoreFocus = false,
    lightDismiss = false,
    showTitleBar = true,
  } = props;
  const { colors } = useTheme();
  const { useV2Components } = useFlags<MaggieFeatureFlags>();

  const _onClose = useCallback(() => {
    if (!lightDismiss) {
      return;
    }
    onClose?.();
  }, [lightDismiss, onClose]);

  if (!useV2Components) {
    return (
      <Dialog
        disableRestoreFocus={disableRestoreFocus}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        onClose={_onClose}
        open={open}
        sx={sx}
      >
        {showTitleBar && (
          <DialogTitle sx={{ paddingX: '2.5rem', paddingTop: '1rem' }}>
            <Title>
              {titleComponent ? titleComponent : <Typography>{title}</Typography>}
              {onClose ? (
                <IconButton
                  aria-label='close'
                  onClick={onClose}
                  sx={{
                    color: colors.neutral[40],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              ) : null}
            </Title>
          </DialogTitle>
        )}
        <DialogContent sx={{ padding: 0 }}>{children}</DialogContent>
      </Dialog>
    );
  }

  return (
    <V2Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && _onClose()}>
      <V2DialogContent
        data-max-width={String(maxWidth)}
        className={cn(fullWidth && 'w-full')}
        showCloseButton={false}
        finalFocus={disableRestoreFocus ? false : undefined}
        aria-label={showTitleBar ? undefined : title}
      >
        {/* The title is body-size, as the MUI branch's Typography; the theme's dialog title is 20px. */}
        {showTitleBar && (
          <V2DialogHeader className='flex-row items-center justify-between px-10 pt-4 pb-0'>
            {titleComponent ? (
              titleComponent
            ) : (
              <V2DialogTitle style={{ fontSize: '1rem' }}>{title}</V2DialogTitle>
            )}
            {onClose ? (
              <IconButton aria-label='close' onClick={onClose} sx={{ color: colors.neutral[40] }}>
                <CloseIcon />
              </IconButton>
            ) : null}
          </V2DialogHeader>
        )}
        <div data-slot='dialog-body' className='p-0'>
          {children}
        </div>
      </V2DialogContent>
    </V2Dialog>
  );
}
